// PlanningPage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useError } from '../contexts/ErrorContext';
import { useChurch } from '../contexts/ChurchContext';
import { useTemplateSync } from '../contexts/TemplateSyncContext';
import { fetchPlanningData, fetchLiturgicalYearWithNextService, PlanningFilters } from '../services/planningService';
import { PlanningData, Service, ServiceSelection, YearChurch } from '../components/planning/types';
import Navigation from '../components/Navigation/Navigation';
import { PerformanceTypeFilter, MusicFilterState } from '../components/Header/PerformanceTypeFilter';
import { ChurchToggles, YearMenu } from '../components/planning/YearChurches';
import { ServiceList } from '../components/planning/ServiceCard/components/ServiceList';
import DirectorView from '../components/planning/DirectorView/DirectorView';
import { API_BASE_URL } from '../config/api';
import apiClient from '../utils/apiClient';
import { Download, Calendar, LayoutList, LayoutGrid } from 'lucide-react';
import '../pages/PlanningPage.css';
import { PlanningNoticesProvider, PlanningNoticeActions } from '../components/planning/PlanningNotices';
import { SongRemindersProvider } from '../components/planning/SongReminders';

/** The churches that have services in the year a planning load is showing. */
const churchesIn = (data: PlanningData | null): YearChurch[] =>
  data ? (data.yearChurches ?? []).filter(c => c.year_id === data.currentLiturgicalYear.id) : [];

const PlanningPage: React.FC = () => {
  const [planningData, setPlanningData] = useState<PlanningData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Churches switched off in the year on screen. Reset to none on every year change.
  const [hiddenChurchIds, setHiddenChurchIds] = useState<number[]>([]);
  // The churches you want on by default in the current year (saved preference).
  const defaultChurchIds = useRef<number[]>([]);
  // The year that holds today, learnt from the first load (which asks for no year).
  const todayYearId = useRef<number | null>(null);
  const [filterState, setFilterState] = useState<MusicFilterState>({
    hideServiceMusic: false,
    selectedPerformanceTypes: []
  });
  const [viewMode, setViewMode] = useState<'detail' | 'director'>('detail');
  const { setError } = useError();
  // Church context available if needed for other features
  useChurch();
  const { lastTemplateUpdate } = useTemplateSync();
  const servicesContainerRef = useRef<HTMLDivElement>(null);
  const [editedServiceId, setEditedServiceId] = useState<number | null>(null);
  const hasScrolledToCurrentDate = useRef(false);
  // The year currently on screen. Every reload passes it explicitly: a request
  // that omits the year makes the server fall back to today's year, which is how
  // changing the church filter used to throw you silently back to the present.
  const viewedYearId = useRef<number | null>(null);

  /**
   * Load one liturgical year, every church in it. Narrowing to a church is done
   * here on the page, so the data for a year is always the whole year.
   */
  const loadPlanningData = useCallback(async (yearId?: number) => {
    try {
      setIsLoading(true);
      const filters: PlanningFilters = {};
      if (yearId) {
        filters.liturgicalYear = yearId.toString();
      }
      const data = await fetchPlanningData(filters);
      if (!yearId) {
        todayYearId.current = data.currentLiturgicalYear.id;
      }
      setPlanningData(data);
      viewedYearId.current = data.currentLiturgicalYear.id;
      // In the current year, start from the saved default; everywhere else, from everyone.
      const inYear = churchesIn(data);
      const defaults = defaultChurchIds.current;
      let hidden: number[] = [];
      if (data.currentLiturgicalYear.id === todayYearId.current && defaults.length > 0 && inYear.length > 1) {
        hidden = inYear.filter(c => !defaults.includes(c.church_id)).map(c => c.church_id);
        if (hidden.length === inYear.length) hidden = [];
      }
      setHiddenChurchIds(hidden);
    } catch (error) {
      setError(error instanceof Error ? error : new Error('An unknown error occurred'));
    } finally {
      setIsLoading(false);
    }
  }, [setError]);

  // Load the user's saved default services, then the planning data for them.
  // One effect, one request: two effects racing here is what made the year jump.
  useEffect(() => {
    const loadDefaultServices = async () => {
      try {
        const response = await apiClient.get('/users/preferences/default-services');
        const defaults: { churchId: number }[] = response.data.defaultServices || [];
        defaultChurchIds.current = Array.from(new Set(defaults.map(d => d.churchId)));
      } catch (error) {
        console.error('Failed to load default services:', error);
      }
      await loadPlanningData(undefined);
    };

    loadDefaultServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const yearChurches = churchesIn(planningData);
  const visibleChurchIds = yearChurches.map(c => c.church_id).filter(id => !hiddenChurchIds.includes(id));
  const isCurrentYear = planningData?.currentLiturgicalYear.id === todayYearId.current;
  const defaultDiffers = isCurrentYear && yearChurches.length > 1 && (
    visibleChurchIds.length !== defaultChurchIds.current.length
    || visibleChurchIds.some(id => !defaultChurchIds.current.includes(id)));

  // Save the churches now on in the current year as the default for next time.
  const handleMakeDefault = async () => {
    if (!planningData) return;
    const services = visibleChurchIds.map(churchId => ({
      churchId,
      serviceDefId: planningData.planningData.services.find(sv => sv.church_id === churchId)?.service_def_id ?? 0,
    }));
    try {
      await apiClient.put('/users/preferences/default-services', { services });
      defaultChurchIds.current = visibleChurchIds;
      setHiddenChurchIds(h => [...h]);
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Could not save the default'));
    }
  };

  // The year's services, less the churches switched off.
  const visibleServices = planningData
    ? (hiddenChurchIds.length === 0
        ? planningData.planningData.services
        : planningData.planningData.services.filter(sv => sv.church_id === undefined || !hiddenChurchIds.includes(sv.church_id)))
    : [];

  // Reload data when templates are updated on Service Music Templates page
  useEffect(() => {
    if (lastTemplateUpdate && planningData) {
      console.log('\ud83d\udd04 Template update detected, refreshing planning data...');
      loadPlanningData(viewedYearId.current ?? undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastTemplateUpdate]);

  // Scroll to edited service after data reload
  useEffect(() => {
    if (editedServiceId && !isLoading && planningData) {
      // Give a small delay to ensure DOM is updated
      setTimeout(() => {
        const serviceElement = document.getElementById(`service-${editedServiceId}`);
        if (serviceElement) {
          serviceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Clear the edited service ID after scrolling
          setEditedServiceId(null);
        }
      }, 100);
    }
  }, [editedServiceId, isLoading, planningData]);

  // Auto-scroll to current date on initial page load
  useEffect(() => {
    if (!hasScrolledToCurrentDate.current && !isLoading && planningData && viewMode === 'detail') {
      hasScrolledToCurrentDate.current = true;
      // Give a small delay to ensure DOM is rendered
      setTimeout(() => {
        // Create UTC date for today at midnight to match service date comparison
        const now = new Date();
        const currentDateUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
        // Find the next upcoming service (today or future) among the churches shown
        const nextService = planningData.planningData.services.find(service => {
          if (service.church_id !== undefined && hiddenChurchIds.includes(service.church_id)) return false;
          const dateOnly = service.liturgy_date.split('T')[0];
          const [year, month, day] = dateOnly.split('-').map(Number);
          const serviceDate = new Date(Date.UTC(year, month - 1, day));
          return serviceDate >= currentDateUTC;
        });

        if (nextService) {
          const serviceElement = document.getElementById(`service-${nextService.service_id}`);
          if (serviceElement) {
            serviceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }, 200);
    }
  }, [isLoading, planningData, viewMode, hiddenChurchIds]);

  // Function to scroll to the next service
  const scrollToCurrentDate = async () => {
    if (!planningData || isLoading) return;

    // Create UTC date for today at midnight to match service date comparison
    const now = new Date();
    const currentDateUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const services = visibleServices;

    // Check if current date is within the currently displayed year
    const hasCurrentDateInYear = services.some(service => {
      // Parse date components to avoid timezone issues
      const dateOnly = service.liturgy_date.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      const serviceDate = new Date(Date.UTC(year, month - 1, day));
      return serviceDate >= currentDateUTC;
    });

    // If current date is not in this year, query the backend for the correct year
    if (!hasCurrentDateInYear) {
      try {
        // Get church IDs if filters are applied
        const churchIds = hiddenChurchIds.length > 0 ? visibleChurchIds : undefined;

        const targetYear = await fetchLiturgicalYearWithNextService(churchIds);

        if (targetYear && targetYear.id !== planningData.currentLiturgicalYear.id) {
          // Switch to the year that contains the next service
          await loadPlanningData(targetYear.id);
          // After loading, scroll to the current date
          setTimeout(() => {
            scrollToCurrentService();
          }, 500);
          return;
        } else if (!targetYear) {
          // No upcoming services found
          setError(new Error('No upcoming services found'));
          return;
        }
      } catch (error) {
        console.error('Error finding liturgical year with next service:', error);
        setError(error instanceof Error ? error : new Error('Failed to find next service'));
        return;
      }
    }

    // Jumping to today must never change which churches you are looking at.
    // This used to clear the church filter "temporarily" and never put it back,
    // so one tap on Today undid a deliberate choice to view a single church.
    // The year we are on already came from fetchLiturgicalYearWithNextService()
    // with those same churches, so there is nothing left to widen.
    scrollToCurrentService();
  };

  const scrollToCurrentService = () => {
    if (!planningData) return;

    // Create UTC date for today at midnight to match service date comparison
    const now = new Date();
    const currentDateUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const services = visibleServices;

    // Find the next service (today or future)
    const nextService = services.find(service => {
      // Parse date components to avoid timezone issues
      const dateOnly = service.liturgy_date.split('T')[0];
      const [year, month, day] = dateOnly.split('-').map(Number);
      const serviceDate = new Date(Date.UTC(year, month - 1, day));
      return serviceDate >= currentDateUTC;
    });

    if (nextService) {
      const serviceElement = document.getElementById(`service-${nextService.service_id}`);
      if (serviceElement) {
        serviceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleYearChange = (yearId: number) => {
    loadPlanningData(yearId);
  };

  const handleServiceUpdate = (updatedService: Service) => {
    if (!planningData) return;

    setPlanningData(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        planningData: {
          ...prev.planningData,
          services: prev.planningData.services.map(service =>
            service.service_id === updatedService.service_id ? updatedService : service
          )
        }
      };
    });
  };

  const handleSelectionsUpdate = async (serviceId: number, updatedSelections: ServiceSelection[]) => {
    if (!planningData) return;

    // Store the edited service ID for scrolling after reload
    setEditedServiceId(serviceId);

    // Stay exactly where we are: same year. The hidden churches are page state and survive the reload.
    const hidden = hiddenChurchIds;
    await loadPlanningData(planningData.currentLiturgicalYear.id);
    setHiddenChurchIds(hidden);
  };

  // Note: allLiturgicalYears is sorted DESC (newest first), so:
  // - "Prev" (older year) = higher index in array
  // - "Next" (newer year) = lower index in array
  const handlePrevYear = () => {
    if (!planningData) return;
    const currentIndex = planningData.allLiturgicalYears.findIndex(year => year.id === planningData.currentLiturgicalYear.id);
    if (currentIndex < planningData.allLiturgicalYears.length - 1) {
      handleYearChange(planningData.allLiturgicalYears[currentIndex + 1].id);
    }
  };

  const handleNextYear = () => {
    if (!planningData) return;
    const currentIndex = planningData.allLiturgicalYears.findIndex(year => year.id === planningData.currentLiturgicalYear.id);
    if (currentIndex > 0) {
      handleYearChange(planningData.allLiturgicalYears[currentIndex - 1].id);
    }
  };

  const handleExportCurrentYear = () => {
    console.log('Export current year clicked');
    if (!planningData) {
      console.log('No planning data available');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found');
      setError(new Error('Please log in to export data'));
      return;
    }
    
    const yearId = planningData.currentLiturgicalYear.id;
    const url = `${API_BASE_URL.replace('/api', '')}/api/hymn-selections-export/year/${yearId}?token=${token}`;
    console.log('Opening URL:', url);
    window.open(url, '_blank');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleExportAllYears = () => {
    console.log('Export all years clicked');
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found');
      setError(new Error('Please log in to export data'));
      return;
    }

    const url = `${API_BASE_URL.replace('/api', '')}/api/hymn-selections-export/all?token=${token}`;
    console.log('Opening URL:', url);
    window.open(url, '_blank');
  };

  return (
    <SongRemindersProvider><PlanningNoticesProvider><div className="planning-page">
      <Navigation />

      {/* One sticky bar under the navigation: where and when on the left, what you are doing on the right */}
      <div className="sticky-headers-wrapper">
        {planningData && (
          <div className="planning-header">
            <div className="planning-where">
              <div className="planning-year-nav">
                <button
                  className="year-nav-btn"
                  onClick={handlePrevYear}
                  aria-label="Previous year"
                  disabled={planningData.allLiturgicalYears.findIndex(year => year.id === planningData.currentLiturgicalYear.id) >= planningData.allLiturgicalYears.length - 1}
                >
                  ‹
                </button>
                <YearMenu
                  years={planningData.allLiturgicalYears}
                  current={planningData.currentLiturgicalYear}
                  yearChurches={planningData.yearChurches ?? []}
                  onPick={handleYearChange}
                />
                <button
                  className="year-nav-btn"
                  onClick={handleNextYear}
                  aria-label="Next year"
                  disabled={planningData.allLiturgicalYears.findIndex(year => year.id === planningData.currentLiturgicalYear.id) <= 0}
                >
                  ›
                </button>
              </div>
              <ChurchToggles
                churches={yearChurches}
                hidden={hiddenChurchIds}
                onChange={setHiddenChurchIds}
                isCurrentYear={isCurrentYear}
                defaultDiffers={defaultDiffers}
                onMakeDefault={handleMakeDefault}
              />
            </div>

            <div className="planning-actions">
              <button
                className="action-button"
                onClick={() => setViewMode(viewMode === 'detail' ? 'director' : 'detail')}
                title={viewMode === 'detail' ? 'Switch to Director View' : 'Switch to Detail View'}
              >
                {viewMode === 'detail' ? <LayoutGrid size={16} /> : <LayoutList size={16} />}
                <span className="action-text">{viewMode === 'detail' ? 'Director' : 'Detail'}</span>
              </button>

              {viewMode === 'detail' && (
                <button className="action-button" onClick={scrollToCurrentDate} title="Go to today">
                  <Calendar size={16} />
                  <span className="action-text">Today</span>
                </button>
              )}

              <button
                className="action-button d-none d-md-flex"
                onClick={handleExportCurrentYear}
                title={`Export CSV for ${planningData.currentLiturgicalYear.year}`}
              >
                <Download size={16} />
                <span className="action-text">Export</span>
              </button>

              <PerformanceTypeFilter
                filterState={filterState}
                onFilterChange={setFilterState}
              />
            </div>
          </div>
        )}
      </div>

      <div className="planning-content" ref={servicesContainerRef}>
        <PlanningNoticeActions />
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : !planningData ? (
          <div className="alert alert-warning" role="alert">No planning data available.</div>
        ) : viewMode === 'director' ? (
          <DirectorView
            services={visibleServices}
            serviceSelections={planningData.planningData.serviceSelections}
            filterState={filterState}
            currentLiturgicalYear={planningData.currentLiturgicalYear}
            allLiturgicalYears={planningData.allLiturgicalYears}
            churchIds={hiddenChurchIds.length > 0 ? visibleChurchIds : undefined}
          />
        ) : (
          <ServiceList
            services={visibleServices}
            serviceSelections={planningData.planningData.serviceSelections}
            spanishSelections={planningData.planningData.spanishSelections ?? []}
            readings={planningData.planningData.readings}
            onServiceUpdate={handleServiceUpdate}
            onSelectionsUpdate={handleSelectionsUpdate}
            filterState={filterState}
          />
        )}
      </div>
    </div></PlanningNoticesProvider></SongRemindersProvider>
  );
};

export default PlanningPage;
